import { GRAPH_INTERVAL } from "@const/pageResponse.constants";
import { GraphInterval } from "@dto/pageResponse.dto";
import { Segmented, Select } from "antd";
import { RangePickerProps } from "antd/es/date-picker";
import dayjs from "dayjs";
import { capitalize } from "lodash-es";
import { FC, useEffect, useRef, useState } from "react";
import { MdLegendToggle } from "react-icons/md";
import { CustomGraphInterval, SyncBoxSlice, useAppGlobStore } from "src/appGlobalStore";
import { XmButton } from "src/component/button/XmButton";
import { RANGE_PICKER_PRESETS } from "src/component/dataTimePicker/rangePickerPresets";
import XmRangeDatePicker, { XmRangeDatePickerRef } from "src/component/dataTimePicker/XmRangeDatePicker";
import { DomUtil } from "src/util/DomUtil";
import { setShownGraphWindow } from './shownGraphWindow';
import './XmGraphControls.less';

const DEFAULT_CUSTOM_RANGE_HOURS = 48;
const CUSTOM = 'custom' as CustomGraphInterval;
const CUSTOM_TITLE = 'Custom range';

/** Presets a box may offer, in GRAPH_INTERVAL order. A bad list falls back to all, never to none. */
export const resolveGraphIntervals = (intervals?: GraphInterval[]): GraphInterval[] => {
  const all = Object.values(GRAPH_INTERVAL);
  if (!intervals?.length) return all;
  const kept = all.filter(i => intervals.includes(i));
  return kept.length ? kept : all;
};

type RangePreset = NonNullable<RangePickerProps['presets']>[number];

/**
 * TODO: Pickers render in the BROWSER's zone while every other timestamp is in the profile's, so
 * they disagree when those differ - this one offers 9:48 for an axis labelling that instant 7:48.
 * A profile zone matching the machine's makes OFFSET zero and hides all of it for now. Bending the
 * values here only put this control out of step with every other picker, so fix it once in
 * XmRangeDatePicker the way XmTableUtilDatePicker does; OFFSET then leaves GraphPlotUtil too.
 */

/** Which presets each interval reaches back to, so Custom offers the same range as the Segmented. */
const RANGE_PRESETS: Record<GraphInterval, RangePreset[]> = {
  day: [RANGE_PICKER_PRESETS.lastHour, RANGE_PICKER_PRESETS.last24h],
  week: [RANGE_PICKER_PRESETS.lastWeek],
  month: [RANGE_PICKER_PRESETS.lastMonth],
  year: [RANGE_PICKER_PRESETS.lastYear],
};

/** Start and end in seconds, 0 while no custom range has been picked. */
export const DEFAULT_GRAPH_CONTROLS: SyncBoxSlice = { interval: GRAPH_INTERVAL.day, showLegend: false, start: 0, end: 0 };

interface XmGraphControlsProps {
  intervals?: GraphInterval[];
  /** Makes the controls controlled, for a parent that remounts them but must keep what the user picked. */
  value?: SyncBoxSlice;
  onChange?: (value: SyncBoxSlice) => void;
}

const XmGraphControls: FC<XmGraphControlsProps> = ({ intervals, value: controlled, onChange }) => {
  const [uncontrolled, setUncontrolled] = useState(DEFAULT_GRAPH_CONTROLS);
  const current = controlled ?? uncontrolled;
  const { interval, showLegend, start, end } = current;
  const [rangeOpen, setRangeOpen] = useState(false);
  const pickerRef = useRef<XmRangeDatePickerRef>(null);

  const allowed = resolveGraphIntervals(intervals);
  // The initial `day` may not be allowed, so publish the clamp. State stays diverged on purpose —
  // the UI offers no disallowed value anyway.
  const effective = interval === CUSTOM || allowed.includes(interval as GraphInterval) ? interval : allowed[0];

  useEffect(() => {
    useAppGlobStore.setState({ syncBoxCtx: { interval: effective, showLegend, start: start / 1000, end: end / 1000 } });
    // The shown window, for the Xbot chat, while these controls are mounted (shownGraphWindow.ts).
    setShownGraphWindow(effective === CUSTOM ? { start: start / 1000, end: end / 1000 } : { preset: effective });
    return () => setShownGraphWindow(null);
  }, [effective, showLegend, start, end]);

  useEffect(() => {
    if (rangeOpen) pickerRef.current?.focus();
  }, [rangeOpen]);

  const update = (patch: Partial<SyncBoxSlice>) => {
    const next = { ...current, ...patch };
    if (!controlled)
      setUncontrolled(next);

    onChange?.(next);
  };

  const applyCustomRange = (startMs: number, endMs: number) => update({ interval: CUSTOM, start: startMs / 1000, end: endMs / 1000 });

  const openCustomRange = () => {
    setRangeOpen(true);
    if (!(start || end)) {
      const now = dayjs();
      applyCustomRange(now.subtract(DEFAULT_CUSTOM_RANGE_HOURS, 'hour').valueOf(), now.valueOf());
    }
  };

  const onSelectValue = (next: CustomGraphInterval) => {
    if (next === CUSTOM) {
      openCustomRange();
    } else {
      setRangeOpen(false);
      update({ interval: next });
    }
  };

  // Segmented fires no change when the active option is re-picked — its radio is already checked
  // (Select has onSelect). Must be the click FORWARDED to that radio, not the label's own: React
  // commits synchronously, so the earlier one mounts the picker for the forwarded one to close.
  const onSegmentedClick = (e: React.MouseEvent) => {
    if (rangeOpen || effective !== CUSTOM || !(e.target instanceof HTMLInputElement))
      return;

    if (e.target.closest('label')?.querySelector(`[title="${CUSTOM_TITLE}"]`))
      openCustomRange();
  };

  // Emitted once on OK; the `onOpenChange(false)` that follows unmounts the picker.
  const confirmRange = (vals: Parameters<NonNullable<RangePickerProps['onChange']>>[0]) => {
    if (!vals?.[0] || !vals[1])
      return;

    const from = vals[0].valueOf();
    const to = vals[1].valueOf();

    // Trim a tail past now — presets end at endOf('day'). Never at the cost of ordering: an
    // inverted range fetches nothing.
    const now = Date.now();
    applyCustomRange(from, to > now && now > from ? now : to);
  };

  const onLegendSwitch = () => update({ showLegend: !showLegend });
  const value = rangeOpen ? CUSTOM : effective;
  const options = [
    { label: 'Custom', value: CUSTOM, title: CUSTOM_TITLE },
    ...allowed.map(i => ({ label: capitalize(i), value: i })),
  ];

  return (
    <div className="xm-graph-controls">
      <XmButton icon={<MdLegendToggle />} type="text" title="Switch graphs legend visibility" onClick={onLegendSwitch} />
      <Segmented
        className="xm-controls-full"
        title="Interval for page"
        value={value}
        options={options}
        onChange={onSelectValue}
        onClick={onSegmentedClick}
      />
      <Select<CustomGraphInterval>
        className="xm-graph-controls-select xm-controls-compact"
        title="Interval for page"
        value={value}
        popupMatchSelectWidth={false}
        options={options}
        onSelect={onSelectValue}
      />
      {rangeOpen &&
        <div className="xm-graph-controls-range">
          <XmRangeDatePicker
            allowClear={false}
            onChange={confirmRange}
            ref={pickerRef}
            defaultOpen
            presets={allowed.flatMap(i => RANGE_PRESETS[i])}
            defaultValue={start && end ? [dayjs.unix(start), dayjs.unix(end)] : undefined}
            showTime={{ defaultOpenValue: [dayjs().startOf('day'), dayjs().endOf('day')] }}
            classNames={{ popup: { root: "xm-graph-controls-range-dropdown" } }}
            getPopupContainer={triggerNode => DomUtil.closestScrollablePopupHost(triggerNode) || triggerNode.parentElement || document.body}
            onOpenChange={nextOpen => { if (!nextOpen) setRangeOpen(false); }}
          />
        </div>
      }
    </div>
  );
};

export default XmGraphControls;
